import { useState } from 'react';
import { useIntl } from 'react-intl';
import { VStack } from '@astryxdesign/core/VStack';
import { TextInput } from '@astryxdesign/core/TextInput';
import { Button } from '@astryxdesign/core/Button';
import { Banner } from '@astryxdesign/core/Banner';
import { api } from '../api/client';
import { authErrorMessage } from './errors';

export function ChangePasswordForm({ onDone }: { onDone: () => void }) {
  const intl = useIntl();
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [repeat, setRepeat] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const mismatch = repeat.length > 0 && repeat !== next;

  const submit = async () => {
    if (busy || !current || next.length < 12 || mismatch) return;
    setBusy(true);
    setError(null);
    try {
      await api('/api/auth/password', { method: 'POST', body: { current, next } });
      setCurrent('');
      setNext('');
      setRepeat('');
      onDone();
    } catch (e) {
      setError(authErrorMessage(intl, e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        void submit();
      }}
    >
      <VStack gap={3}>
        {error ? <Banner status="error" title={error} collapsible={false} /> : null}
        <TextInput
          label={intl.formatMessage({ id: 'auth.password.current' })}
          type="password"
          value={current}
          onChange={setCurrent}
          autoComplete="current-password"
          width="100%"
        />
        <TextInput
          label={intl.formatMessage({ id: 'auth.password.next' })}
          description={intl.formatMessage({ id: 'auth.password.rules' })}
          type="password"
          value={next}
          onChange={setNext}
          autoComplete="new-password"
          width="100%"
        />
        <TextInput
          label={intl.formatMessage({ id: 'auth.password.repeat' })}
          type="password"
          value={repeat}
          onChange={setRepeat}
          autoComplete="new-password"
          width="100%"
          {...(mismatch
            ? {
                status: {
                  type: 'error' as const,
                  message: intl.formatMessage({ id: 'auth.password.mismatch' }),
                },
              }
            : {})}
        />
        <Button
          type="submit"
          variant="primary"
          label={intl.formatMessage({ id: 'auth.password.submit' })}
          isLoading={busy}
          isDisabled={next.length < 12 || mismatch}
        />
      </VStack>
    </form>
  );
}
